/* Potentially connects a bridge component version of a controller to an element.
   Prerequisites:
   - A 'normal' stimulus controller needs to be exist and connected to the element.
   - The bridge version of the controller should be in the 'bridge' subdirectory and named the same as the
     normal version, unless targetControllerName overrides the destination controller.
*/
export function maybeConnectBridge(element, component = null, targetControllerName = null) {
  // Don't do anything if we're not in a hotwire-native app

  if (!document.querySelector('[data-hotwire-native]')) {
    return false;
  }

  // Default to the bridge component's name being the same as the controller.
  if (component == null) {
    component = element.dataset.controller;
  }

  // If the app running this view doesn't declare support for the component, fall back to web.
  // Load-bearing for rollout safety: old binaries don't declare newer component names.
  if (!appSupportsComponent(component)) {
    return false;
  }

  // Default to the bridge controller living in a 'bridge' subdirectory of the web controller.
  if (targetControllerName == null) {
    targetControllerName = bridgifyController(element.dataset.controller);
  }

  modifyAttributes(element, element.dataset.controller, targetControllerName);

  element.dataset.controller = targetControllerName;

  return true;
}

// Components the app declares in its user agent - also the signal behind
// BridgeComponent.shouldLoad, so this agrees with whether the bridge controller was registered.
// Not supportsComponent(): that answers from the adapter, which installs a native round trip
// later, so a hand-off decided in connect() on a full document load could lose that race and
// strand the element on a web player the native shell hides. Messages sent before the adapter
// arrives are queued and flushed on setAdapter, so handing off early is safe.
function appSupportsComponent(component) {
  const declared = window.navigator.userAgent.match(/bridge-components: \[(.*?)\]/);

  return !!declared && declared[1].split(" ").includes(component);
}

// We split by -- (representing the directory structure), and inject 'bridge' before the last part (assuming that
// the bridge is in a subdirectory of the controller)
function bridgifyController(controllerName) {
  const controllerParts = controllerName.split("--")
  controllerParts.splice(-1, 0, "bridge");
  return controllerParts.join("--");
}

function modifyAttribute(element, controllerName, targetControllerName) {
  Object.values(element.attributes).filter(attribute => attribute.name.startsWith(`data-${controllerName}`)).forEach(attribute => {
    element.setAttribute(attribute.name.replace(controllerName, targetControllerName), attribute.value);
    element.removeAttribute(attribute.name);
  });
}

function modifyAttributes(element, controllerName, targetControllerName) {
  // Modify everything that starts with data-${this.controller}
  modifyAttribute(element, controllerName, targetControllerName);

  // Recursively modify all child elements
  element.querySelectorAll(`*`).forEach(e => {
    modifyAttribute(e, controllerName, targetControllerName);
  });

  // Modify any actions that include the controller reference
  element.querySelectorAll(`[data-action]`).forEach(element => {
    if (element.dataset.action.includes(controllerName)) {
      element.dataset.action = element.dataset.action.replaceAll(controllerName, targetControllerName);
    }
  });
}
